/**
 * Event handlers for suggestions and editor lifecycle
 */

import {EditorView} from "@codemirror/view";
import {EditorState} from "@codemirror/state";
import {suggestionsField, setTokensEffect} from "../state/editor.state";
import {suggestionQuickMenu} from "../suggestions/suggestion-menu";
import {tokenContextMenu} from "../menus/token-context-menu";

/**
 * Create suggestion click handler
 */
export function createSuggestionClickHandler() {
  return EditorView.domEventHandlers({
    click: (event, view) => {
      const target = event.target as HTMLElement;
      const suggestionMark = target.closest(".suggestion-mark");

      if (!suggestionMark) return false;

      event.preventDefault();
      event.stopPropagation();

      const pos = view.posAtDOM(suggestionMark);
      const suggestions = view.state.field(suggestionsField);

      const suggestion = suggestions.find((s) => s.from <= pos && pos < s.to);

      if (suggestion) {
        suggestionQuickMenu.show(view, suggestion, suggestionMark as HTMLElement);
      }

      return true;
    },
  });
}

/**
 * Create blur event handler
 */
export function createBlurHandler(
  onBlurCallback: (serialized: string) => void,
  serializeFunc: (state: EditorState) => string,
  emitOnBlur: boolean,
  scheduleEmitFunc: (view: EditorView) => void
) {
  return EditorView.domEventHandlers({
    blur: (_event, view) => {
      onBlurCallback(serializeFunc(view.state));

      if (emitOnBlur) {
        scheduleEmitFunc(view);
      }

      return false;
    },
  });
}

/**
 * Create change update listener
 */
export function createChangeListener(
  emitOnBlur: boolean,
  scheduleEmitFunc: (view: EditorView) => void
) {
  return EditorView.updateListener.of((u) => {
    if (emitOnBlur) return;

    const tokensReplaced = u.transactions.some((tr) =>
      tr.effects?.some((e) => e.is(setTokensEffect))
    );

    if (!u.docChanged && !tokensReplaced) return;

    scheduleEmitFunc(u.view);
  });
}

/**
 * Create selection context menu handler.
 *
 * Shows a small menu when the user right-clicks with a non-empty selection.
 */
export function createSelectionTokenMenuHandler() {
  return EditorView.domEventHandlers({
    contextmenu: (event, view) => {
      const sel = view.state.selection.main;
      if (sel.empty) return false;

      event.preventDefault();
      event.stopPropagation();

      const from = Math.min(sel.from, sel.to);
      const to = Math.max(sel.from, sel.to);

      // Try to anchor to the selection. coordsAtPos can return null in some edge cases.
      const fromRect = view.coordsAtPos(from);
      const toRect = view.coordsAtPos(to);

      let anchor: DOMRect;
      if (fromRect && toRect) {
        const left = Math.min(fromRect.left, toRect.left);
        const right = Math.max(fromRect.right, toRect.right);
        const top = Math.min(fromRect.top, toRect.top);
        const bottom = Math.max(fromRect.bottom, toRect.bottom);
        anchor = new DOMRect(left, top, right - left, bottom - top);
      } else {
        // Fallback to mouse position.
        anchor = new DOMRect(event.clientX, event.clientY, 0, 0);
      }

      tokenContextMenu.show(view, {from, to}, anchor);
      return true;
    },
  });
}
